import {
  Firestore,
  doc,
  getDoc,
  writeBatch,
  serverTimestamp
} from "firebase/firestore";
import { createOffer, updateOffer, deleteOffer, getOffersByLocationId } from "./offerCrud";
import { CampLocation } from "@/types/campLocation";
import {
  describeInvalidPlaceholderException,
  placeholderBaseData,
  placeholderCountFanOutIds,
  placeholderDates,
  planPlaceholderSync
} from "./placeholderSyncCore";

/**
 * Resolve number of days from a decimalEnums document keyed by offer increment key (e.g., 'oi_5day')
 */
async function resolveNumDaysFromIncrementKey(db: Firestore, key?: string | null): Promise<number | null> {
  if (!key) return null;
  try {
    const enumRef = doc(db, "decimalEnums", key);
    const snap = await getDoc(enumRef);
    if (snap.exists()) {
      const data: any = snap.data();
      const val = typeof data?.value === "number" ? data.value : null;
      return Number.isFinite(val) ? (val as number) : null;
    }
  } catch {
    // best-effort only
  }
  return null;
}

/**
 * Ensure there is at most one placeholder offer per program (variation.key) that appears on
 * real sessions: non-placeholder offers that end today UTC or later, or ended within the last
 * 13 calendar months (UTC). A variation whose `placeholderDiscoveryUntil` is today (UTC) or
 * later — and no later than the expiry rule could have produced — also qualifies, with or without
 * real sessions (see placeholderDiscoveryException.ts).
 * - Creates missing placeholders
 * - Updates placeholders when program defaults / increment keys change
 * - Deletes placeholders for keys no longer in the discovery set
 * Only operates on offers with isPlaceholder === true
 *
 * What to do is decided by `placeholderSyncCore.ts`, which the camp-ingest publish script runs too;
 * this function only reads and writes.
 */
export async function ensurePlaceholderOffersForLocation(
  db: Firestore,
  locationId: string,
  userId: string
): Promise<{ created: number; updated: number; deleted: number; }> {
  let created = 0, updated = 0, deleted = 0;

  const locRef = doc(db, "campLocations", locationId);
  const locSnap = await getDoc(locRef);
  if (!locSnap.exists()) {
    throw new Error(`Location not found: ${locationId}`);
  }
  const location = locSnap.data() as CampLocation;

  // Only manage placeholders for published locations
  if (location.visibility !== "v_published") {
    return { created, updated, deleted };
  }

  const allOffers = await getOffersByLocationId(db, locationId);
  const plan = planPlaceholderSync(location, allOffers, new Date());
  if (!plan) return { created, updated, deleted };
  for (const bad of plan.invalidExceptions) {
    console.warn(describeInvalidPlaceholderException(locationId, bad));
  }

  const createdIds: string[] = [];
  const deletedIds = new Set<string>();

  for (const { variationKey, incrementKey, existingId } of plan.upserts) {
    const numDays = await resolveNumDaysFromIncrementKey(db, incrementKey);
    const baseData: any = placeholderBaseData(variationKey, incrementKey, placeholderDates(numDays));

    if (!existingId) {
      const createdOffer = await createOffer(db, locationId, baseData, userId);
      if (createdOffer?.id) createdIds.push(createdOffer.id);
      created += 1;
    } else {
      await updateOffer(db, existingId, baseData, userId);
      updated += 1;
    }
  }

  for (const id of plan.deletes) {
    await deleteOffer(db, id);
    deletedIds.add(id);
    deleted += 1;
  }

  if (plan.countChanged) {
    const batch = writeBatch(db);
    batch.update(doc(db, "campLocations", locationId), {
      placeholderOffersCount: plan.newCount,
      lastEditedAt: serverTimestamp()
    });

    // Offers as they stand after this sync: a batch update on a just-deleted placeholder would fail
    // the whole commit, and a just-created one was stamped with the location's previous count.
    for (const offerId of placeholderCountFanOutIds(allOffers.map(o => o.id), deletedIds, createdIds)) {
      batch.update(doc(db, "offers", offerId), { numPlaceholderOffers: plan.newCount });
    }

    await batch.commit();
  }

  return { created, updated, deleted };
}

